import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CORE_ROLES, ROLE_METADATA, UserRole } from '@/components/authnz/types/rbac';
import type { PrototypeAction, PrototypeRow } from './types';
import { problemLabel } from './fake-store';
import { DiagnosticBlock, GrantChips, LifecycleBadge } from './parts';

export const VARIANT_C_NAME = 'Access inbox';

export function VariantC({
  rows,
  onAction,
}: {
  rows: PrototypeRow[];
  onAction: (action: PrototypeAction) => void;
  stepUpOk: boolean;
}) {
  const [healthyQuery, setHealthyQuery] = useState('');
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.TUTOR);

  const problems = rows.filter((row) => problemLabel(row));
  const healthy = rows.filter((row) => !problemLabel(row));
  const picked = rows.find((row) => row.emailKey === pickedKey) ?? problems[0] ?? null;

  const healthyHits = useMemo(
    () =>
      healthyQuery
        ? healthy.filter((row) => row.emailKey.includes(healthyQuery.toLowerCase()))
        : [],
    [healthy, healthyQuery],
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_24rem]">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Primary surface is emails that cannot get in. Healthy identities stay behind search.
          No Ban. Auth-disabled-without-deleted is a card, not a delete.
        </p>
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Invite email"
            value={inviteEmail}
            onChange={(event) => setInviteEmail(event.target.value)}
            className="max-w-xs"
          />
          <select
            className="h-10 rounded-md border border-input bg-background px-2 text-sm"
            value={inviteRole}
            onChange={(event) => setInviteRole(event.target.value as UserRole)}
          >
            {CORE_ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_METADATA[role].displayName}
              </option>
            ))}
          </select>
          <Button
            type="button"
            onClick={() => {
              if (inviteEmail.trim()) {
                onAction({
                  type: 'invite',
                  emailKey: inviteEmail.trim().toLowerCase(),
                  role: inviteRole,
                });
                setInviteEmail('');
              }
            }}
          >
            Invite
          </Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {problems.map((row) => (
            <Card
              key={row.emailKey}
              className={`cursor-pointer ${picked?.emailKey === row.emailKey ? 'ring-2 ring-ring' : ''}`}
              onClick={() => setPickedKey(row.emailKey)}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{row.emailKey}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <LifecycleBadge row={row} />
                <p className="text-sm text-muted-foreground">{problemLabel(row)}</p>
                <GrantChips grants={row.grants} />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="rounded-lg border border-border p-3">
          <p className="mb-2 text-sm font-medium">Find a healthy email</p>
          <Input
            placeholder="Type to search existing emails with no problems"
            value={healthyQuery}
            onChange={(event) => setHealthyQuery(event.target.value)}
          />
          <ul className="mt-2 space-y-1">
            {healthyHits.map((row) => (
              <li key={row.emailKey}>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => setPickedKey(row.emailKey)}
                >
                  {row.emailKey}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <aside className="space-y-3 rounded-lg border border-border bg-card p-4">
        {picked ? (
          <>
            <h2 className="text-lg font-semibold">{picked.emailKey}</h2>
            <GrantChips
              grants={picked.grants}
              onRevokeAdmin={(role) =>
                onAction({ type: 'revoke-admin', emailKey: picked.emailKey, role })
              }
            />
            <div className="flex flex-wrap gap-2">
              {picked.lifecycle === 'deleted' ? (
                <Button
                  type="button"
                  onClick={() => onAction({ type: 'restore', emailKey: picked.emailKey })}
                >
                  Restore then assign
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => onAction({ type: 'delete', emailKey: picked.emailKey })}
                >
                  Delete
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => onAction({ type: 'copy-link', emailKey: picked.emailKey })}
              >
                Copy link
              </Button>
            </div>
            <DiagnosticBlock row={picked} />
          </>
        ) : (
          <p className="text-muted-foreground">No problem selected.</p>
        )}
      </aside>
    </div>
  );
}
